/*
 * Notification centre endpoints (th-b2c-techupgrade-be-notification).
 *
 * Route: dashboard -> be-admin -> be-notification, the same hop every other admin action
 * takes. be-admin forwards service-to-service; be-notification's admin routes refuse any
 * request whose id-token carries a `customerId` claim, so the customer-facing gateway can
 * never reach them.
 *
 * Point REACT_APP_API_NOTIFICATION_URL at be-notification directly to work against a
 * local service; left empty it falls back to REACT_APP_API_ADMIN_URL.
 */

const trim = value => (value || "").trim().replace(/\/+$/, "");

export const NOTIFICATION_API_BASE =
  trim(process.env.REACT_APP_API_NOTIFICATION_URL) || trim(process.env.REACT_APP_API_ADMIN_URL);

/*
 * be-admin carries these routes under its own admin-actions prefix, which is the tree the
 * API Gateway fronts for that service; be-notification's own prefix is /api/v1/admin, so
 * pointing REACT_APP_API_NOTIFICATION_URL straight at it needs this variable set too.
 */
export const NOTIFICATION_ADMIN_PATH = (
  process.env.REACT_APP_API_NOTIFICATION_ADMIN_PATH || "/api/v1/admin-actions/notifications"
).trim();

export const NOTIFICATION_ADMIN_URL = `${NOTIFICATION_API_BASE}${NOTIFICATION_ADMIN_PATH}`;

/*
 * How a call reaches be-admin.
 *
 * The eleven dedicated routes are the real contract, but each is a new path and answers
 * nothing until someone creates an API Gateway resource for it. The push-notification send
 * path already has one, so by default every action is posted there with a `notificationAction`
 * naming it, and be-admin dispatches on that - the same workaround the NAV reads use with
 * `?nav_view=`, and the reason neither needs a resource of its own.
 *
 * Set REACT_APP_NOTIFICATION_DEDICATED_ROUTES=true once the resources exist, or when talking
 * to a service directly with no gateway in between, as a local run does.
 */
export const CARRIER_PATH = (
  process.env.REACT_APP_API_PUSH_PATH || "/api/v1/admin-actions/push-notification/send"
).trim();

export const CARRIER_URL = `${NOTIFICATION_API_BASE}${CARRIER_PATH}`;

export const USE_DEDICATED_ROUTES =
  (process.env.REACT_APP_NOTIFICATION_DEDICATED_ROUTES || "").trim().toLowerCase() === "true";

const DEDICATED = {
  list: () => ({ method: "GET", tail: "" }),
  get: args => ({ method: "GET", tail: `/${encodeURIComponent(args.id)}` }),
  create: () => ({ method: "POST", tail: "" }),
  update: args => ({ method: "PUT", tail: `/${encodeURIComponent(args.id)}` }),
  setStatus: args => ({ method: "PUT", tail: `/${encodeURIComponent(args.id)}/status` }),
  send: args => ({ method: "POST", tail: `/${encodeURIComponent(args.id)}/send` }),
  remove: args => ({ method: "DELETE", tail: `/${encodeURIComponent(args.id)}` }),
  previewAudience: () => ({ method: "POST", tail: "/preview-audience" }),
  surveys: () => ({ method: "GET", tail: "/surveys" }),
  surveySummary: args => ({ method: "GET", tail: `/surveys/${encodeURIComponent(args.code)}/summary` }),
  surveyResponses: args => ({ method: "GET", tail: `/surveys/${encodeURIComponent(args.code)}/responses` })
};

/** A filter the operator left empty is left out, rather than sent blank. */
export function buildQueryString(params) {
  const parts = [];
  Object.keys(params || {}).forEach(key => {
    const value = params[key];
    if (value === undefined || value === null || value === "") {
      return;
    }
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  });
  return parts.length > 0 ? `?${parts.join("&")}` : "";
}

/**
 * One request, described rather than built at each call site.
 *
 * Returns what fetch needs - url, method, body - so that which of the two ways in is used is
 * decided here and nowhere else.
 */
export const notificationRequest = (action, args = {}) => {
  if (USE_DEDICATED_ROUTES) {
    const route = DEDICATED[action](args);
    return {
      url: `${NOTIFICATION_ADMIN_URL}${route.tail}${buildQueryString(args.query)}`,
      method: route.method,
      body: args.payload
    };
  }

  return {
    url: CARRIER_URL,
    method: "POST",
    body: {
      notificationAction: action,
      ...(args.id ? { id: args.id } : {}),
      ...(args.code ? { code: args.code } : {}),
      ...(args.query ? { query: args.query } : {}),
      ...(args.payload ? { payload: args.payload } : {})
    }
  };
};

/* Options of the Create / Edit form. The values are be-notification's enums. */

export const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" }
];

export const CONTENT_TYPE_OPTIONS = [
  { value: "GENERAL", label: "General" },
  { value: "PROMO", label: "Promo" },
  { value: "URGENT", label: "Urgent" }
];

export const NOTIFICATION_TYPE_OPTIONS = [
  { value: "NORMAL", label: "Normal" },
  { value: "SILENT", label: "Silent" }
];

export const ACTION_TYPE_OPTIONS = [
  { value: "NONE", label: "None" },
  { value: "URL", label: "Open link (https)" },
  { value: "DEEPLINK", label: "Open in-app page (deeplink)" },
  { value: "SURVEY", label: "Open survey (CSAT / NPS)" }
];

export const USER_SCOPE_OPTIONS = [
  { value: "ALL", label: "All User" },
  { value: "MF", label: "MF User" },
  { value: "PVD", label: "PVD User" },
  { value: "NON_USER", label: "Non user" },
  { value: "EMAIL_LIST", label: "By email" },
  { value: "CUSTOMER_LIST", label: "By customer list" }
];

/* Survey codes be-notification serves (src/config/SurveyDefinition.ts) */
export const SURVEY_OPTIONS = [
  { value: "CSAT_APP_V1", label: "CSAT — rate the app experience" },
  { value: "NPS_QUARTERLY_V1", label: "NPS — quarterly" }
];

export const SEND_STATUS_LABEL = {
  DRAFT: "Draft",
  PENDING: "Pending",
  SENDING: "Sending",
  SENT: "Sent",
  PARTIAL: "Partially sent",
  FAILED: "Failed",
  CANCELLED: "Cancelled"
};

/** Headers every admin call carries: the admin's own session, exactly like the other pages. */
export const authHeaders = (accessToken, tGUID) => ({
  "Content-Type": "application/json",
  userid: tGUID,
  "x-id-token": "Bearer " + accessToken
});

/** fetch options for a described request. Headers are the caller's - every page builds its own. */
export const notificationInit = (request, headers) => ({
  method: request.method,
  headers,
  ...(request.body === undefined ? {} : { body: JSON.stringify(request.body) })
});

/** Reads whatever the backend answered with ({ ERROR_CODE, MESSAGE } or plain text). */
export const readError = async request => {
  try {
    const body = await request.text();
    if (!body) {
      return `HTTP ${request.status}`;
    }
    try {
      const parsed = JSON.parse(body);
      return parsed.MESSAGE || parsed.message || parsed.ERROR_CODE || body;
    } catch (error) {
      return body.slice(0, 300);
    }
  } catch (error) {
    return `HTTP ${request.status}`;
  }
};
